import { isElectron } from '~/utils/common'

/**
 * Partner (reseller / white-label) identifiers. The white-label desktop build
 * stamps one as its PARTNER_ID, and a PBX carries one in `pbx.partner.key`. Keep
 * in sync with apps/electron-app/partner-configs.
 */
export enum Partners {
  MaxAssist = 'maxassist',
  Dentrix = 'dentrix',
  'Dentrix-Ascend' = 'dentrix-ascend',
  Dentally = 'dentally',
  Denticon = 'denticon',
  Default = '',
}

export type PartnerId = `${Partners}`

export const isValidPartnerId = (partnerId: string): partnerId is PartnerId =>
  Object.values(Partners).includes(partnerId as Partners)

/**
 * Resolves the active partner the way the app brands itself: the white-label
 * desktop build's partner (window.electron.getPartnerId) wins over the PBX's
 * partner key, since a branded build is the stronger signal of who the user is.
 * Both are validated; the desktop `default` build and an unknown key resolve to
 * '' (no partner).
 */
export const resolvePartnerId = (
  electronPartnerId: string | null | undefined,
  pbxPartnerKey: string | null | undefined,
): PartnerId => {
  if (electronPartnerId && isValidPartnerId(electronPartnerId)) {
    return electronPartnerId
  }
  const key = pbxPartnerKey ?? ''
  return isValidPartnerId(key) ? key : ''
}

/** Whether this is a desktop build that can report its own partner. */
export const canLookupInstalledAppPartner = (): boolean =>
  isElectron && Boolean(window.electron?.getPartnerId)

/**
 * The installed desktop build's partner (window.electron.getPartnerId), never
 * the PBX's. Resolves '' for the base build (no partner id, or 'default'), and
 * null when the lookup is unavailable, throws, or returns an unknown id.
 */
export const lookupInstalledAppPartner =
  async (): Promise<PartnerId | null> => {
    const getPartnerId = window.electron?.getPartnerId
    if (!canLookupInstalledAppPartner() || !getPartnerId) return null
    try {
      const id = await getPartnerId()
      if (!id || id === 'default') return ''
      return isValidPartnerId(id) ? id : null
    } catch (error) {
      console.warn('Failed to get partner ID from Electron:', error)
      return null
    }
  }

/**
 * The installed desktop build's partner, never the PBX's. Resolves '' for the
 * base build, the web app, an unknown id, or a failed lookup.
 */
export const fetchInstalledAppPartner = async (): Promise<PartnerId> =>
  (await lookupInstalledAppPartner()) ?? ''
